import React from 'react';
import * as Sentry from '@sentry/react';

interface ErrorFallbackProps {
  error: unknown;
  componentStack?: string;
  eventId?: string;
  resetError?: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  eventId,
  resetError,
}) => {
  const errorMessage =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
      ? error
      : 'An unexpected error occurred.';

  const handleOpenFeedback = () => {
    if (eventId) {
      Sentry.showReportDialog({ eventId });
    }
  };

  const handleReload = () => {
    if (resetError) {
      resetError();
    }
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-gray-100 p-6 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-50 text-red-600 rounded-full flex items-center justify-center text-3xl">
          <i className="ri-error-warning-line"></i>
        </div>

        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Something went wrong
        </h2>

        <p className="text-sm text-gray-500 mb-4">
          An error occurred while loading this page. Our team has been notified.
        </p>

        {process.env.NODE_ENV !== 'production' && (
          <div className="text-left bg-gray-50 border border-gray-200 rounded p-3 mb-4 text-xs font-mono text-red-700 overflow-x-auto max-h-36">
            {errorMessage}
          </div>
        )}

        {eventId && (
          <p className="text-xs text-gray-400 mb-4 font-mono">
            Event ID: {eventId}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <button
            type="button"
            onClick={handleReload}
            className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors inline-flex items-center justify-center gap-1.5"
          >
            <i className="ri-refresh-line"></i>
            Reload Page
          </button>

          {eventId && (
            <button
              type="button"
              onClick={handleOpenFeedback}
              className="w-full sm:w-auto px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors inline-flex items-center justify-center gap-1.5"
            >
              <i className="ri-feedback-line"></i>
              Report Issue
            </button>
          )}

          <a
            href="/"
            className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg transition-colors inline-flex items-center justify-center gap-1.5"
          >
            <i className="ri-home-line"></i>
            Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
